// Export des écritures au format que le cabinet comptable importe réellement.
//
// POURQUOI UN SECOND FORMAT. Le module `export-comptable` produit un journal
// des ventes calqué sur un autre export Extrabat. Le cabinet d'IMP TOITURE
// (UNITIO) ne sait pas l'importer : il l'a refusé en septembre 2026. Le format
// ci-dessous est repris LIGNE À LIGNE du fichier d'exemple envoyé par la
// directrice du cabinet — pas d'une documentation, du fichier lui-même.
//
//   M;01MOINOTLA;VE;000;20260706; ;FA0000564 - MOINOT Laure;D;490000;;…;F0000564
//   M;44571110;VE;000;20260706; ;FA0000564 - MOINOT Laure;C;44545;;…;F0000564
//   M;70611100;VE;000;20260706; ;FA0000564 - MOINOT Laure;C;445455;;…;F0000564
//   …
//   C;01MOINOTLA;MOINOT Laure;…;41100000;…;1;C;;
//
// DEUX TYPES DE LIGNES. Les `M` sont les mouvements, en tête de fichier ; les
// `C` sont les fiches clients, à la fin, une par client cité. Une fiche
// rattache le compte auxiliaire du client (`01MOINOTLA`) au compte collectif
// (`41100000`).
//
// CE QUI SURPREND, et qu'il faut respecter :
//
//   les montants sont en CENTIMES, sans virgule ni signe — c'est la colonne
//   `D`/`C` qui porte le sens. Un avoir n'a donc pas de montant négatif : il a
//   les sens inversés ;
//   la date s'écrit AAAAMMJJ ;
//   la pièce fait 8 caractères : l'initiale du document suivie de ses 7
//   chiffres (FA0000564 -> F0000564, AV0000039 -> A0000039) ;
//   les colonnes vides ne sont pas vides : ce sont des largeurs fixes
//   d'espaces, relevées sur le fichier d'exemple et reproduites telles quelles ;
//   le fichier est en UTF-8 sans BOM, et se termine par des CRLF.
//
// Fonctions PURES : pas d'accès réseau, pas d'horloge, tout est paramètre.

import { cleTaux } from './export-comptable.js';
import type { FactureExport } from './export-comptable.js';

/** Les comptes du cabinet, taux par taux. Posés avec lui, jamais devinés. */
export interface ComptesGcn {
  /** Code du journal des ventes. */
  journal: string;
  /** Compte client collectif, porté par les fiches `C`. */
  clientCollectif: string;
  /** '20' -> { vente: '70611400', tva: '44571140' } */
  parTaux: Record<string, { vente: string; tva: string }>;
}

/**
 * Les comptes d'IMP TOITURE, confirmés par le cabinet le 21/09/2026.
 *
 * Le fichier d'exemple portait aussi un compte `70610900`, toujours à zéro,
 * sur lequel le cabinet ne s'est pas prononcé. Il n'est donc PAS utilisé : un
 * compte dont on ignore le rôle ne se remplit pas au jugé.
 */
export const COMPTES_GCN_DEFAUT: ComptesGcn = {
  journal: 'VE',
  clientCollectif: '41100000',
  parTaux: {
    '20': { vente: '70611400', tva: '44571140' },
    '10': { vente: '70611100', tva: '44571110' },
    '5.5': { vente: '70611300', tva: '44571130' },
  },
};

export interface LigneGcn {
  compte: string;
  /** AAAAMMJJ */
  date: string;
  libelle: string;
  sens: 'D' | 'C';
  /** Entier POSITIF, en centimes. Le sens porte le signe. */
  centimes: number;
  piece: string;
}

export interface FicheClientGcn {
  compte: string;
  nom: string;
}

export interface AvertissementGcn {
  piece: string;
  message: string;
}

export interface ResultatGcn {
  lignes: LigneGcn[];
  fiches: FicheClientGcn[];
  avertissements: AvertissementGcn[];
  nbPieces: number;
}

/** Un montant en euros devient un entier de centimes, arrondi au plus proche. */
export function centimesGcn(euros: number): number {
  return Math.round(Math.abs(euros) * 100);
}

/** yyyy-mm-dd -> AAAAMMJJ, sans passer par Date : aucun décalage horaire possible. */
export function dateGcn(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[1] ?? ''}${m[2] ?? ''}${m[3] ?? ''}` : iso.replace(/\D/g, '').slice(0, 8);
}

/**
 * La pièce : huit caractères, l'initiale du document puis sept chiffres.
 *
 *   FA0000564     -> F0000564    Extrabat : un compteur, rien d'autre
 *   AV0000039     -> A0000039
 *   FAC-2026-0004 -> F2600004    le CRM : année sur deux chiffres, puis rang
 *
 * DEUX NUMÉROTATIONS, DEUX TRAITEMENTS. Extrabat numérote à la suite depuis
 * toujours : ses sept chiffres tiennent tels quels. Le CRM repart à 1 chaque
 * année, donc le rang seul se répéterait d'une année sur l'autre — et deux
 * pièces différentes portant le même numéro, c'est le genre d'erreur qu'un
 * comptable met un trimestre à démêler. L'année est donc gardée, ramenée à
 * deux chiffres faute de place.
 *
 * Cette seconde règle n'a PAS été validée par le cabinet : aucun numéro du CRM
 * ne figure dans son fichier d'exemple. À lui faire confirmer sur le premier
 * export réel.
 */
export function pieceGcn(numero: string): string {
  const initiale = (numero.trim()[0] ?? 'F').toUpperCase();
  const annee = /^[A-Za-z]+-?(\d{4})-(\d+)$/.exec(numero.trim());
  if (annee) {
    const [, an = '', rang = ''] = annee;
    return initiale + an.slice(2) + rang.slice(-5).padStart(5, '0');
  }
  const chiffres = numero.replace(/\D/g, '');
  return initiale + chiffres.slice(-7).padStart(7, '0');
}

/** Retire ce qui casserait le découpage : le séparateur et les fins de ligne. */
function propre(texte: string): string {
  return texte
    .replace(/[;\r\n]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** « FA0000564 - MOINOT Laure ». */
export function libelleGcn(numero: string, client: string): string {
  return `${propre(numero)} - ${propre(client)}`;
}

/**
 * Le compte auxiliaire PROPOSÉ pour un client : `01` puis huit caractères
 * tirés de son nom, sans accent ni ponctuation, complétés de zéros.
 *
 *   MOINOT Laure -> 01MOINOTLA        HOA -> 01HOA00000
 *
 * ATTENTION : ce n'est qu'une PROPOSITION, pour un client que le cabinet ne
 * connaît pas encore. Sur les vingt-quatre fiches du fichier d'exemple, quatre
 * ne suivent pas cette règle — le compte avait été créé sous une orthographe
 * depuis corrigée (`01BRAISAZC` pour BRAIZAZ Colette, `01ROUESSEC` pour
 * ROUESNE Charles). Un compte auxiliaire est un IDENTIFIANT : il est attribué
 * une fois et ne bouge plus. Le recalculer à chaque export dédoublerait le
 * compte du client dans le grand livre du cabinet, et couperait son solde en
 * deux. D'où `contacts.code_comptable`, qui le fige.
 */
export function compteAuxiliairePropose(client: string): string {
  const sansAccent = client.normalize('NFD').replace(/[̀-ͯ]/g, '');
  const lettres = sansAccent.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  return `01${lettres.slice(0, 8).padEnd(8, '0')}`;
}

/** Un code auxiliaire a la forme attendue par le cabinet. */
export function codeAuxiliaireValide(code: string): boolean {
  return /^01[A-Z0-9]{8}$/.test(code);
}

// Les colonnes « vides » du format ne le sont pas : ce sont des largeurs fixes,
// relevées sur le fichier du cabinet. Les reproduire à l'identique est la seule
// façon d'être sûr que son logiciel retombe sur ses colonnes.
const REMPLISSAGE_M = [
  '',
  ' '.repeat(6),
  ' '.repeat(2),
  ' '.repeat(3),
  ' '.repeat(5),
  ' '.repeat(10),
  ' '.repeat(10),
];
const REMPLISSAGE_C = [
  ' '.repeat(7),
  ' '.repeat(13),
  ' '.repeat(13),
  ' '.repeat(13),
  ' '.repeat(13),
];
const REMPLISSAGE_C_FIN = [' '.repeat(30), ' '.repeat(30), ' '.repeat(30), ' '.repeat(20)];

/**
 * Les écritures d'un lot de factures, prêtes à écrire.
 *
 * Une facture donne : le client au débit pour le TTC, puis la TVA et la vente
 * au crédit, taux par taux. Un avoir donne les mêmes lignes, sens inversés.
 * Les lignes d'une pièce sortent triées par numéro de compte croissant, comme
 * dans le fichier du cabinet — l'auxiliaire `01…` vient donc en premier.
 *
 * Rien n'est inventé : un taux sans comptes configurés n'est pas remplacé par
 * un compte au hasard, il est SIGNALÉ et la pièce est laissée de côté.
 */
export function ecrituresGcn(factures: FactureExport[], comptes: ComptesGcn): ResultatGcn {
  const lignes: LigneGcn[] = [];
  const fiches: FicheClientGcn[] = [];
  const vues = new Set<string>();
  const avertissements: AvertissementGcn[] = [];
  let nbPieces = 0;

  for (const f of factures) {
    const piece = pieceGcn(f.numero);
    const avoir = f.kind === 'avoir';
    const client = propre(f.client);

    const code = f.compteClient ?? '';
    if (code === '') {
      avertissements.push({
        piece,
        message: `${f.numero} — ${client} n'a pas de compte comptable. Proposition : ${compteAuxiliairePropose(client)}. À faire valider par le cabinet avant le premier export.`,
      });
      continue;
    }
    if (!codeAuxiliaireValide(code)) {
      avertissements.push({
        piece,
        message: `${f.numero} — le compte de ${client} (« ${code} ») n'a pas la forme attendue par le cabinet (01 + 8 caractères).`,
      });
      continue;
    }

    // Les bases de TVA figées à l'émission font foi : c'est ce qui est écrit
    // sur la facture remise au client, pas un recalcul d'aujourd'hui.
    const bases = f.detailTva.filter((d) => d.baseHt !== 0 || d.montantTva !== 0);
    const inconnus = bases.filter((d) => comptes.parTaux[cleTaux(d.taux)] === undefined);
    if (inconnus.length > 0) {
      avertissements.push({
        piece,
        message: `${f.numero} — aucun compte configuré pour le taux ${inconnus.map((d) => `${cleTaux(d.taux)} %`).join(', ')}. Pièce non exportée.`,
      });
      continue;
    }

    const date = dateGcn(f.dateEmission);
    const libelle = libelleGcn(f.numero, client);
    const aLigne = (compte: string, sens: 'D' | 'C', euros: number): void => {
      lignes.push({ compte, date, libelle, sens, centimes: centimesGcn(euros), piece });
    };

    const desPieces: LigneGcn[] = [];
    const avant = lignes.length;
    aLigne(code, avoir ? 'C' : 'D', f.montantTtc);
    for (const d of bases) {
      const cpt = comptes.parTaux[cleTaux(d.taux)];
      if (cpt === undefined) continue;
      if (d.montantTva !== 0) aLigne(cpt.tva, avoir ? 'D' : 'C', d.montantTva);
      if (d.baseHt !== 0) aLigne(cpt.vente, avoir ? 'D' : 'C', d.baseHt);
    }
    desPieces.push(...lignes.splice(avant));
    desPieces.sort((a, b) => a.compte.localeCompare(b.compte));

    // Une pièce déséquilibrée est refusée par le logiciel du cabinet, et c'est
    // la première chose qu'il regarde. On la signale plutôt que de l'envoyer.
    const debit = desPieces.filter((l) => l.sens === 'D').reduce((t, l) => t + l.centimes, 0);
    const credit = desPieces.filter((l) => l.sens === 'C').reduce((t, l) => t + l.centimes, 0);
    if (debit !== credit) {
      avertissements.push({
        piece,
        message: `${f.numero} — écriture déséquilibrée de ${((debit - credit) / 100).toFixed(2)} €. Pièce non exportée.`,
      });
      continue;
    }

    lignes.push(...desPieces);
    nbPieces += 1;
    if (!vues.has(code)) {
      vues.add(code);
      fiches.push({ compte: code, nom: client });
    }
  }

  return { lignes, fiches, avertissements, nbPieces };
}

/** Le fichier, tel que le cabinet l'attend : UTF-8 sans BOM, lignes en CRLF. */
export function csvGcn(resultat: ResultatGcn, comptes: ComptesGcn): string {
  const out: string[] = [];
  for (const l of resultat.lignes) {
    out.push(
      [
        'M',
        l.compte,
        comptes.journal,
        '000',
        l.date,
        ' ',
        l.libelle,
        l.sens,
        String(l.centimes),
        ...REMPLISSAGE_M,
        l.piece,
      ].join(';'),
    );
  }
  for (const fi of resultat.fiches) {
    out.push(
      [
        'C',
        fi.compte,
        fi.nom,
        ...REMPLISSAGE_C,
        comptes.clientCollectif,
        ...REMPLISSAGE_C_FIN,
        '1',
        'C',
        '',
        '',
      ].join(';'),
    );
  }
  return out.map((l) => `${l}\r\n`).join('');
}

/** « ecritures-2026-08.csv » — le mois porté par le fichier, pas la date du jour. */
export function nomFichierGcn(debut: string, fin: string): string {
  const d = debut.slice(0, 7);
  return d === fin.slice(0, 7) ? `ecritures-${d}.csv` : `ecritures-${debut}_${fin}.csv`;
}
